import { useMemo, useState } from "react";
import { PlatformSource, RecentAnswer, getStoreLinkRecord } from "winslow-core";
import { getDateDaysAgo } from "winslow-core";
import EscalationsChart from "./EscalationsChart";
import { ViewToggle } from "./ViewToggle";
import { CollectionStatsQuestions } from "./CollectionStatsQuestions";
import { AskedQuestionsZero } from "../collections/AskedQuestionsZero";
import { SourceSelector } from "../SourceSelector";
import { NameSelector } from "../NameSelector/NameSelector";
import { useSelector } from "react-redux";
import { selectLinkRecords } from "../../redux/features/appData";

type EscalationStatsProps = {
  answers: RecentAnswer[];
  onRefresh: () => void;
};

export const EscalationStats = ({
  answers,
  onRefresh,
}: EscalationStatsProps) => {
  const [view, setView] = useState<"cards" | "list">("cards");
  const [nameFilter, setNameFilter] = useState<string | undefined>(undefined);
  const [sourceFilter, setSourceFilter] = useState<PlatformSource | undefined>(
    undefined
  );
  const linkRecords = useSelector(selectLinkRecords);

  const escalatedAnswers = useMemo(
    () => (answers || []).filter((a) => a.requiredEscalation),
    [answers]
  );

  const filteredForList = useMemo(() => {
    const filtered = escalatedAnswers
      .filter((a) => {
        if (!sourceFilter) return true;
        return a.platformSource === sourceFilter;
      })
      .filter((a) => {
        if (!nameFilter) return true;
        return a.name === nameFilter;
      })
      .sort((a, b) => {
        const ad = new Date(a.createdAt).getTime();
        const bd = new Date(b.createdAt).getTime();
        return bd - ad; // most recent first
      });

    return filtered;
  }, [escalatedAnswers, sourceFilter, nameFilter]);

  const escalationsForChartL30 = useMemo(() => {
    const last30Start = getDateDaysAgo(30);
    return filteredForList.filter((a) => new Date(a.createdAt) >= last30Start);
  }, [filteredForList]);

  const exportEscalationsCSV = () => {
    // Determine max number of documents across filtered answers based on message.sources
    const maxDocs = filteredForList.reduce((max, a) => {
      const count = a.message?.sources?.length || 0;
      return Math.max(max, count);
    }, 0);

    const header = [
      "User Email",
      "User Name",
      "Required Escalation",
      "Question",
      "Response",
      "Platform Source",
      "Created At",
      ...Array.from({ length: maxDocs }).flatMap((_, i) => [
        `Document ${i + 1} Title`,
        `Document ${i + 1} URL`,
      ]),
    ];

    const csvRows = filteredForList.map((a) => {
      const userEmail = a.email || "";
      const userName = a.name || "";
      const escalationStatus = a.requiredEscalation ? "Yes" : "No";

      // Build document title/url pairs dynamically from message.sources
      const sourceIds: string[] = a.message?.sources || [];
      const docPairs: string[] = [];
      for (let i = 0; i < maxDocs; i++) {
        const id = sourceIds[i];
        if (id) {
          const lr = getStoreLinkRecord(id, linkRecords || []);
          const primaryLink = lr?.links?.[0];
          docPairs.push(primaryLink?.title || "", primaryLink?.url || "");
        } else {
          docPairs.push("", "");
        }
      }

      return [
        userEmail,
        userName,
        escalationStatus,
        a.question?.replaceAll("\n", " ") || "",
        a.answer?.replaceAll("\n", " ") || "",
        a.platformSource || "",
        new Date(a.createdAt).toISOString(),
        ...docPairs,
      ];
    });

    const csv = [header, ...csvRows]
      .map((r) =>
        r
          .map((v) => `"${(v ?? "").toString().replaceAll('"', '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "escalations.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!answers || answers.length === 0) {
    return <AskedQuestionsZero />;
  }

  if (escalatedAnswers.length === 0) {
    return <AskedQuestionsZero />;
  }

  return (
    <div className="collection-stats-tab">
      <div className="collection-stats-summary-chart">
        <EscalationsChart
          answers={escalationsForChartL30}
          endDate={new Date()}
          startDate={getDateDaysAgo(30)}
        />
      </div>

      <div className="collection-stats-filter">
        <NameSelector responses={escalatedAnswers} onChange={setNameFilter} />
        <SourceSelector
          responses={escalatedAnswers}
          onChange={setSourceFilter}
        />
        <div className="view-container">
          <button className="export-csv-btn" onClick={exportEscalationsCSV}>
            Export CSV
          </button>
        </div>
        <ViewToggle view={view} setView={setView} />
      </div>

      <div className="collection-stats-feedback">
        <CollectionStatsQuestions
          questions={filteredForList}
          filterName={nameFilter}
          filterSource={sourceFilter}
          view={view}
        />
      </div>
    </div>
  );
};

export default EscalationStats;
